import "./AgentScoringRulesTable.less";

import {
    GuxButton,
    GuxIcon,
    GuxTable,
    GuxToggle,
} from "genesys-spark-components-react";
import {
    AgentScoringRuleAgentToScoreEnum,
    AgentScoringRuleSamplingTypeEnum,
} from "@genesys/cloud-frontend/models";
import type { AgentScoringRule } from "@genesys/cloud-frontend/models";
import { useStrings } from "features/i18n/useStrings";
import strings from "./AgentScoringRulesTable.strings";
import commonStrings from "./agentScoringRulesCommon.strings";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
    selectCanDeleteScoringRule,
    selectCanEditScoringRule,
    selectCanViewScoringRule,
} from "features/auth/currentUserSlice";
import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";
import FeatureToggle from "enums/FeatureToggle";
import {
    selectAgentScoringRules,
    selectTogglingRuleIdSet,
    setRuleEnabled,
} from "./agentScoringRulesSlice";
import { selectEvaluationFormNames } from "features/evaluationForms/evaluationFormNamesSlice";
import agentScoringRulesToasts from "./agentScoringRulesToasts";
import { DEFAULT_AGENT_TO_SCORE_WHEN_MISSING } from "./agentScoringRuleUtils";
import { toGuxDisabled } from "util/componentUtil";

interface AgentScoringRulesTableProps {
    programId: string;
    /** When true and the table has no rows, show the load-failed empty message. */
    loadFailed?: boolean;
    onEditRule: (ruleId: string) => void;
    onDeleteRule: (rule: AgentScoringRule) => void;
}

export function AgentScoringRulesTable({
    programId,
    loadFailed = false,
    onEditRule,
    onDeleteRule,
}: AgentScoringRulesTableProps) {
    const t = useStrings(strings);
    const common = useStrings(commonStrings);
    const dispatch = useAppDispatch();

    const canView = useAppSelector(selectCanViewScoringRule);
    const rulesFromStore = useAppSelector(selectAgentScoringRules);
    // Without view permission, show an empty table (do not load or display rules).
    const rules = canView ? rulesFromStore : [];
    const formNames = useAppSelector(selectEvaluationFormNames);
    // Memoized Set from the slice — O(1) per-row lookup (same as selectIsRuleToggling).
    const togglingRuleIds = useAppSelector(selectTogglingRuleIdSet);

    const canEdit = useAppSelector(selectCanEditScoringRule);
    const canDelete = useAppSelector(selectCanDeleteScoringRule);
    const isEvaluationFocusEnabled = useAppSelector((state) =>
        selectFeatureToggle(state, FeatureToggle.WEM_1511_EVALUATION_FOCUS)
    );
    const isDivisionAwareEnabled = useAppSelector((state) =>
        selectFeatureToggle(state, FeatureToggle.DIVISION_AWARE_EVALS_SURVEYS)
    );

    const samplingLabel = (rule: AgentScoringRule): string =>
        rule.samplingType === AgentScoringRuleSamplingTypeEnum.All
            ? t.labels.samplingType.all
            : t.labels.samplingType.percentage;

    const agentsToScoreLabel = (rule: AgentScoringRule): string => {
        const agentToScore =
            rule.agentToScore ?? DEFAULT_AGENT_TO_SCORE_WHEN_MISSING;
        switch (agentToScore) {
            case AgentScoringRuleAgentToScoreEnum.Each:
                return common.labels.agentsToScore.each;
            case AgentScoringRuleAgentToScoreEnum.First:
                return common.labels.agentsToScore.first;
            case AgentScoringRuleAgentToScoreEnum.Last:
            default:
                return common.labels.agentsToScore.last;
        }
    };

    const resolveFormName = (
        contextId: string | undefined
    ): { text: string; restricted: boolean } => {
        if (!contextId) {
            return { text: "", restricted: false };
        }
        const name = formNames[contextId];
        if (name) {
            return { text: name, restricted: false };
        }
        if (isDivisionAwareEnabled) {
            return {
                text: common.labels.restrictedByPermissions,
                restricted: true,
            };
        }
        return { text: contextId, restricted: false };
    };

    const handleToggle = async (
        rule: AgentScoringRule,
        enabled: boolean
    ): Promise<void> => {
        if (!rule.id || !canEdit) return;
        const action = await dispatch(
            setRuleEnabled({ programId, ruleId: rule.id, enabled })
        );
        if (setRuleEnabled.fulfilled.match(action)) {
            agentScoringRulesToasts.updateSuccess();
        } else {
            agentScoringRulesToasts.updateFailure();
        }
    };

    const emptyMessage = !canView
        ? common.labels.accessRestricted
        : loadFailed && rules.length === 0
        ? t.labels.loadFailedMessage
        : t.labels.emptyMessage;

    return (
        <GuxTable
            className="AgentScoringRulesTable"
            emptyMessage={emptyMessage}
            data-testid="agent-scoring-rules-table"
        >
            <table slot="data">
                <thead>
                    <tr>
                        <th>{t.labels.columns.ruleSelectionCriteria}</th>
                        {isEvaluationFocusEnabled && (
                            <th>{common.labels.agentsToScore.label}</th>
                        )}
                        <th>{t.labels.columns.ruleForm}</th>
                        <th>{t.labels.columns.status}</th>
                        <th>{t.labels.columns.actions}</th>
                    </tr>
                </thead>
                <tbody>
                    {rules.map((rule) => {
                        const form = resolveFormName(
                            rule.evaluationFormContextId
                        );
                        const sampling = samplingLabel(rule);
                        const agents = isEvaluationFocusEnabled
                            ? agentsToScoreLabel(rule)
                            : "";
                        const isToggling =
                            !!rule.id && togglingRuleIds.has(rule.id);
                        const editDisabled = toGuxDisabled(!canEdit);
                        const deleteDisabled = toGuxDisabled(!canDelete);
                        return (
                            <tr
                                key={rule.id}
                                data-testid={`agent-scoring-rule-row-${rule.id}`}
                            >
                                <td>
                                    <span
                                        className="AgentScoringRulesTable-cellText"
                                        title={sampling}
                                    >
                                        {sampling}
                                    </span>
                                </td>
                                {isEvaluationFocusEnabled && (
                                    <td>
                                        <span
                                            className="AgentScoringRulesTable-cellText"
                                            title={agents}
                                        >
                                            {agents}
                                        </span>
                                    </td>
                                )}
                                <td>
                                    <span
                                        className={
                                            form.restricted
                                                ? "AgentScoringRulesTable-cellText AgentScoringRulesTable-cellText--restricted"
                                                : "AgentScoringRulesTable-cellText"
                                        }
                                        title={form.text}
                                    >
                                        {form.text}
                                    </span>
                                </td>
                                <td>
                                    <GuxToggle
                                        checked={!!rule.enabled}
                                        disabled={editDisabled}
                                        aria-disabled={editDisabled}
                                        loading={isToggling}
                                        checkedLabel={t.labels.status.enabled}
                                        uncheckedLabel={
                                            t.labels.status.disabled
                                        }
                                        title={
                                            canEdit
                                                ? undefined
                                                : t.labels.actions
                                                      .noEditPermissionTooltip
                                        }
                                        onCheck={(event) =>
                                            void handleToggle(
                                                rule,
                                                event.detail
                                            )
                                        }
                                        data-testid={`agent-scoring-rule-toggle-${rule.id}`}
                                    />
                                </td>
                                <td>
                                    <div className="AgentScoringRulesTable-actions">
                                        <GuxButton
                                            accent="inline"
                                            disabled={editDisabled}
                                            aria-disabled={editDisabled}
                                            title={
                                                canEdit
                                                    ? t.labels.actions
                                                          .editTooltip
                                                    : t.labels.actions
                                                          .noEditPermissionTooltip
                                            }
                                            onClick={() => {
                                                if (!canEdit || !rule.id)
                                                    return;
                                                onEditRule(rule.id);
                                            }}
                                            data-testid={`agent-scoring-rule-edit-${rule.id}`}
                                        >
                                            <GuxIcon
                                                iconName="edit"
                                                screenreaderText={
                                                    canEdit
                                                        ? t.labels.actions
                                                              .editTooltip
                                                        : t.labels.actions
                                                              .noEditPermissionTooltip
                                                }
                                                size="small"
                                            />
                                        </GuxButton>
                                        <GuxButton
                                            accent="inline"
                                            disabled={deleteDisabled}
                                            aria-disabled={deleteDisabled}
                                            title={
                                                canDelete
                                                    ? t.labels.actions
                                                          .deleteTooltip
                                                    : t.labels.actions
                                                          .noDeletePermissionTooltip
                                            }
                                            onClick={() => {
                                                if (!canDelete) return;
                                                onDeleteRule(rule);
                                            }}
                                            data-testid={`agent-scoring-rule-delete-${rule.id}`}
                                        >
                                            <GuxIcon
                                                iconName="delete"
                                                screenreaderText={
                                                    canDelete
                                                        ? t.labels.actions
                                                              .deleteTooltip
                                                        : t.labels.actions
                                                              .noDeletePermissionTooltip
                                                }
                                                size="small"
                                            />
                                        </GuxButton>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </GuxTable>
    );
}

export default AgentScoringRulesTable;
